import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectCurrentSession } from "../../features/sessions/sessions-slice";
import { selectActivePaymentTypes } from "../../features/payment-types/payment-types-slice";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import {
  clearSessionCancellationPolicy,
  fetchSessionCancellationPolicy,
  saveSessionCancellationPolicy,
} from "../../features/sessions/slice-extensions/session-cancellation-policy-slice-extension";
import CancellationPolicyFields, {
  CancellationPolicyValues,
  toPolicyValues,
} from "../../features/organization/components/CancellationPolicyFields";
import { previewInput, toValidRequest } from "../../features/organization/utils/policyPreview";
import { policySentence } from "../../features/portal/utils/policySentence";

interface SessionCancellationPolicyFieldProps {
  disabled?: boolean;
}

/**
 * The Session form's cancellation policy: the organization's by default, or the
 * Session's own override. Saves on its own (the Session must exist first).
 */
const SessionCancellationPolicyField = ({ disabled = false }: SessionCancellationPolicyFieldProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(selectCurrentSession)?.id;
  const paymentTypes = useAppSelector(selectActivePaymentTypes);
  const [values, setValues] = useState<CancellationPolicyValues | null>(null);
  const [useDefault, setUseDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    dispatch(fetchPaymentTypes());
    dispatch(fetchSessionCancellationPolicy(sessionId))
      .unwrap()
      .then((result) => {
        setValues(toPolicyValues(result.policy));
        setUseDefault(!result.isOverride);
      })
      .catch(() => setValues(null));
  }, [dispatch, sessionId]);

  if (!sessionId) {
    return (
      <p className="text-xs text-grey-40 dark:text-grey-80">{t("sessionForm.cancellationPolicy.saveFirst")}</p>
    );
  }
  if (!values) return null;

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      toast.success(t("sessionForm.cancellationPolicy.saved"));
    } catch (rejected) {
      setError((rejected as { message?: string })?.message ?? null);
    } finally {
      setBusy(false);
    }
  };

  const handleUseDefault = (checked: boolean) => {
    setUseDefault(checked);
    if (!checked) return;
    run(async () => {
      await dispatch(clearSessionCancellationPolicy(sessionId)).unwrap();
      const result = await dispatch(fetchSessionCancellationPolicy(sessionId)).unwrap();
      setValues(toPolicyValues(result.policy));
    });
  };

  const handleSave = () => {
    const request = toValidRequest(values);
    if (!request) {
      setError(t("settings.cancellationPolicy.feeRequired"));
      return;
    }
    run(async () => {
      const result = await dispatch(saveSessionCancellationPolicy({ sessionId, request })).unwrap();
      setValues(toPolicyValues(result.policy));
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium text-grey-30 dark:text-grey-70">{t("sessionForm.cancellationPolicy.label")}</p>
      <label className="flex items-center gap-2 text-sm text-grey-20 dark:text-white">
        <input
          type="checkbox"
          checked={useDefault}
          disabled={disabled || busy}
          onChange={(e) => handleUseDefault(e.target.checked)}
        />
        {t("sessionForm.cancellationPolicy.useDefault")}
      </label>
      {useDefault ? (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {policySentence(previewInput(values, paymentTypes), t, i18n.language)}
        </p>
      ) : (
        <>
          <CancellationPolicyFields
            idPrefix="session_cancellation"
            values={values}
            paymentTypes={paymentTypes}
            disabled={disabled || busy}
            onChange={setValues}
          />
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {policySentence(previewInput(values, paymentTypes), t, i18n.language)}
          </p>
          <div>
            <button type="button" className="btn btn-secondary" onClick={handleSave} disabled={disabled || busy}>
              {busy ? t("common.saving") : t("settings.cancellationPolicy.save")}
            </button>
          </div>
        </>
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default SessionCancellationPolicyField;
